import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet, ScrollView,
  ActivityIndicator, Image, Modal, Pressable, Animated, Easing,
  useWindowDimensions,
} from 'react-native';
import {
  doc, setDoc, updateDoc, deleteDoc, serverTimestamp, collection, onSnapshot, orderBy, query,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, storage, secondaryAuth } from '../firebase/firebase';
import { fetchBranches, BranchModel } from '../query/branchService';

const C = {
  primary: '#29A05A', primaryLight: 'rgba(41,160,90,0.15)', success: '#10b981', successLight: 'rgba(16,185,129,0.15)',
  danger: '#ef4444', dangerLight: 'rgba(239,68,68,0.15)', warning: '#f59e0b',
  bg: '#0E1512', text: '#E2EDE6', muted: '#7A9A80', border: 'rgba(255,255,255,0.09)', rowAlt: '#1A2820',
};

const PAGE_SIZE = 10;
const TABLE_MIN_WIDTH = 1120;
const COLS = {
  sn: 0.4,
  holder: 2.4,
  contact: 1.4,
  email: 2.2,
  branch: 1.5,
  joined: 1.3,
  actions: 2.4,
};

type FilterType = 'all' | 'active' | 'inactive';

interface ShareholderForm {
  name: string;
  contact: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const fmtDate = (ts: any): string => {
  try {
    const d = ts?.toDate ? ts.toDate() : null;
    return d ? d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  } catch { return '—'; }
};

// Defined OUTSIDE to keep stable reference across renders — prevents focus loss
const Field: React.FC<{
  label: string;
  field: keyof ShareholderForm;
  placeholder: string;
  value: string;
  error?: string;
  keyboardType?: any;
  readonly?: boolean;
  onChange: (field: keyof ShareholderForm, value: string) => void;
}> = ({ label, field, placeholder, value, error, keyboardType, readonly, onChange }) => (
  <View style={styles.fieldWrap}>
    <Text style={styles.label}>{label}</Text>
    <TextInput
      style={[styles.input, error ? styles.inputError : null, readonly ? { backgroundColor: '#141E18', color: C.muted } : null]}
      placeholder={placeholder}
      placeholderTextColor="#aaa"
      value={value}
      onChangeText={readonly ? undefined : (v) => onChange(field, v)}
      keyboardType={keyboardType}
      editable={!readonly}
      autoCapitalize={field === 'email' ? 'none' : 'words'}
    />
    {error ? <Text style={styles.errorText}>{error}</Text> : null}
  </View>
);

const TBIcon = ({ name }: { name: string }) => (
  <span className="material-icons-round" style={{ fontSize: 18, color: '#4ECDC4', lineHeight: 1 }}>{name}</span>
);

const ShareholderScreen: React.FC = () => {
  const { width } = useWindowDimensions();
  const isMobile = width < 760;

  const [form, setForm] = useState<ShareholderForm>({
    name: '', contact: '', email: '', password: '', confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<ShareholderForm>>({});
  const [shareholders, setShareholders] = useState<any[]>([]);
  const [branches, setBranches] = useState<BranchModel[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [branchError, setBranchError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // List controls
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [page, setPage] = useState(1);

  // Drawer (register + edit)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Delete + toast
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const toastTimer = useRef<any>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  };

  // Drawer slide animation
  const drawerW = Math.min(isMobile ? width : 480, width);
  const slideX = useRef(new Animated.Value(drawerW)).current;
  useEffect(() => {
    if (drawerOpen) {
      slideX.setValue(drawerW);
      Animated.timing(slideX, { toValue: 0, duration: 280, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    }
  }, [drawerOpen, drawerW, slideX]);
  const closeDrawer = () => {
    Animated.timing(slideX, { toValue: drawerW, duration: 220, easing: Easing.in(Easing.cubic), useNativeDriver: true })
      .start(() => setDrawerOpen(false));
  };

  useEffect(() => {
    const q = query(collection(db, 'zapee_share_holders'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setShareholders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsub(); if (toastTimer.current) clearTimeout(toastTimer.current); };
  }, []);

  useEffect(() => { fetchBranches().then(setBranches).catch(console.error); }, []);
  useEffect(() => { setPage(1); }, [search, filter]);

  const branchName = (id?: string) => branches.find(b => b.branchId === id)?.storename || id || '—';

  const handleChange = (field: keyof ShareholderForm, value: string) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: undefined }));
  };

  // ── Derived list ──
  const filtered = useMemo(() => {
    let list = [...shareholders];
    if (filter === 'active') list = list.filter(s => s.active);
    else if (filter === 'inactive') list = list.filter(s => !s.active);
    if (search.trim()) {
      const qq = search.toLowerCase();
      list = list.filter(s =>
        (s.name || '').toLowerCase().includes(qq) ||
        (s.email || '').toLowerCase().includes(qq) ||
        (s.contact || '').toLowerCase().includes(qq)
      );
    }
    return list;
  }, [shareholders, search, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeCount = shareholders.filter(s => s.active).length;
  const inactiveCount = shareholders.length - activeCount;

  const validate = (isEdit: boolean): boolean => {
    const errs: Partial<ShareholderForm> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.contact.trim()) errs.contact = 'Contact is required';
    if (!isEdit) {
      if (!form.email.trim()) errs.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Invalid email address';
      if (!form.password) errs.password = 'Password is required';
      else if (form.password.length < 6) errs.password = 'Minimum 6 characters';
      if (!form.confirmPassword) errs.confirmPassword = 'Please confirm password';
      else if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    }
    setErrors(errs);
    if (!selectedBranchId) { setBranchError('Please select a branch'); return false; }
    setBranchError('');
    return Object.keys(errs).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImageUri(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setForm({ name: '', contact: '', email: '', password: '', confirmPassword: '' });
    setSelectedBranchId('');
    setImageUri(null);
    setImageFile(null);
    setErrors({});
    setBranchError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openCreate = () => {
    resetForm();
    setEditingId(null);
    setDrawerOpen(true);
  };

  const openEdit = (s: any) => {
    setForm({ name: s.name || '', contact: s.contact || '', email: s.email || '', password: '', confirmPassword: '' });
    setSelectedBranchId(s.branchId || '');
    setImageUri(s.profileImage || null);
    setImageFile(null);
    setErrors({});
    setBranchError('');
    setEditingId(s.id);
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    const isEdit = !!editingId;
    if (!validate(isEdit)) return;
    setLoading(true);
    try {
      if (isEdit) {
        // ── Update ── (partial write; never rebuilds the whole doc)
        let profileImageUrl: string | null = imageUri;
        if (imageFile) {
          const storageRef = ref(storage, `Shareholder/${editingId}_${imageFile.name}`);
          await uploadBytes(storageRef, imageFile);
          profileImageUrl = await getDownloadURL(storageRef);
        }
        await updateDoc(doc(db, 'zapee_share_holders', editingId!), {
          name: form.name.trim(),
          contact: form.contact.trim(),
          branchId: selectedBranchId,
          profileImage: profileImageUrl ?? null,
        });
        showToast('Shareholder updated');
      } else {
        // ── Create ── (secondary auth so admin session is preserved)
        const credential = await createUserWithEmailAndPassword(secondaryAuth, form.email.trim(), form.password);
        const uid = credential.user.uid;
        await signOut(secondaryAuth);

        let profileImageUrl: string | null = null;
        if (imageFile) {
          const storageRef = ref(storage, `Shareholder/${uid}_${imageFile.name}`);
          await uploadBytes(storageRef, imageFile);
          profileImageUrl = await getDownloadURL(storageRef);
        }
        await setDoc(doc(db, 'zapee_share_holders', uid), {
          uid,
          name: form.name.trim(),
          contact: form.contact.trim(),
          email: form.email.trim(),
          profileImage: profileImageUrl,
          branchId: selectedBranchId,
          createdAt: serverTimestamp(),
          active: true,
        });
        showToast('Shareholder registered successfully');
      }
      resetForm();
      closeDrawer();
    } catch (error: any) {
      showToast(error.message || 'Failed to save shareholder', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (s: any) => {
    const newVal = !s.active;
    setShareholders(prev => prev.map(x => x.id === s.id ? { ...x, active: newVal } : x));
    try {
      await updateDoc(doc(db, 'zapee_share_holders', s.id), { active: newVal });
    } catch {
      setShareholders(prev => prev.map(x => x.id === s.id ? { ...x, active: !newVal } : x));
      showToast('Failed to update status', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'zapee_share_holders', deleteTarget.id));
      showToast('Shareholder deleted');
      setDeleteTarget(null);
    } catch {
      showToast('Failed to delete shareholder', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const initialOf = (s: any) => (s.name || 'S').charAt(0).toUpperCase();

  // ── Table cells ──
  const Th = ({ label, flex, align }: { label: string; flex: number; align?: 'center' | 'right' }) => (
    <View style={[tb.th, { flex }, align === 'center' && { alignItems: 'center' }, align === 'right' && { alignItems: 'flex-end' }]}>
      <Text style={tb.thTxt}>{label}</Text>
    </View>
  );

  const Row = ({ item, index }: { item: any; index: number }) => (
    <View style={[tb.row, index % 2 === 1 && { backgroundColor: C.rowAlt }]}>
      <View style={[tb.td, { flex: COLS.sn, alignItems: 'center' }]}>
        <Text style={tb.snTxt}>{(safePage - 1) * PAGE_SIZE + index + 1}</Text>
      </View>
      <View style={[tb.td, { flex: COLS.holder, flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
        {item.profileImage
          ? <Image source={{ uri: item.profileImage }} style={tb.avatar} />
          : <View style={[tb.avatar, { backgroundColor: item.active ? C.primary : '#94a3b8', alignItems: 'center', justifyContent: 'center' }]}><Text style={tb.avatarTxt}>{initialOf(item)}</Text></View>}
        <View style={{ flex: 1 }}>
          <Text style={tb.name} numberOfLines={1}>{item.name || 'Unnamed'}</Text>
          <Text style={tb.idTxt} numberOfLines={1}>ID: {item.id}</Text>
        </View>
      </View>
      <View style={[tb.td, { flex: COLS.contact }]}>
        <Text style={tb.cellTxt} numberOfLines={1}>{item.contact || '—'}</Text>
      </View>
      <View style={[tb.td, { flex: COLS.email }]}>
        <Text style={tb.cellTxt} numberOfLines={1}>{item.email || '—'}</Text>
      </View>
      <View style={[tb.td, { flex: COLS.branch }]}>
        <Text style={tb.cellTxt} numberOfLines={1}>{branchName(item.branchId)}</Text>
      </View>
      <View style={[tb.td, { flex: COLS.joined }]}>
        <Text style={tb.cellTxt}>{fmtDate(item.createdAt)}</Text>
      </View>
      <View style={[tb.td, { flex: COLS.actions, flexDirection: 'row', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }]}>
        <TouchableOpacity
          style={[tb.tgl, { backgroundColor: item.active ? C.success : '#141E18', borderColor: item.active ? C.success : C.border }]}
          onPress={() => toggleActive(item)}
        >
          <Text style={[tb.tglTxt, { color: item.active ? '#fff' : C.muted }]}>{item.active ? 'Active' : 'Inactive'}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={tb.editBtn} onPress={() => openEdit(item)}><Text style={tb.editTxt}>Edit</Text></TouchableOpacity>
        <TouchableOpacity style={tb.delBtn} onPress={() => setDeleteTarget(item)}><Text style={tb.delTxt}>Delete</Text></TouchableOpacity>
      </View>
    </View>
  );

  return (
    <View style={styles.screen}>
      {/* Toolbar */}
      <View style={[styles.controls, isMobile && { flexDirection: 'column', alignItems: 'stretch', gap: 10 }]}>
        <View style={styles.tbTitle}>
          <TBIcon name="account_balance" />
          <View>
            <Text style={styles.tbTitleText}>Shareholders</Text>
            <Text style={styles.tbTitleSub}>Equity & payout records</Text>
          </View>
        </View>
        <View style={[styles.searchBox, { flex: 1 }]}>
          <Text style={{ fontSize: 14, marginRight: 8 }}>🔍</Text>
          <TextInput
            style={styles.searchInput}
            placeholder="Search name, email or contact…"
            value={search}
            onChangeText={setSearch}
            placeholderTextColor="#8BAAB8"
          />
          {search ? <TouchableOpacity onPress={() => setSearch('')}><Text style={{ color: '#8BAAB8' }}>✕</Text></TouchableOpacity> : null}
        </View>
        <View style={styles.filterRow}>
          {(['all', 'active', 'inactive'] as FilterType[]).map(f => (
            <TouchableOpacity key={f} style={[styles.filterChip, filter === f && styles.filterChipActive]} onPress={() => setFilter(f)}>
              <Text style={[styles.filterChipTxt, filter === f && styles.filterChipTxtActive]}>{f.charAt(0).toUpperCase() + f.slice(1)}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <TouchableOpacity style={styles.addBtn} onPress={openCreate}>
          <Text style={styles.addBtnTxt}>+ Register Shareholder</Text>
        </TouchableOpacity>
      </View>

      {/* Table */}
      <ScrollView horizontal showsHorizontalScrollIndicator style={{ flex: 1 }} contentContainerStyle={{ minWidth: TABLE_MIN_WIDTH, flexGrow: 1 }}>
        <View style={tb.table}>
          <View style={tb.headerRow}>
            <Th label="#" flex={COLS.sn} align="center" />
            <Th label="Shareholder" flex={COLS.holder} />
            <Th label="Contact" flex={COLS.contact} />
            <Th label="Email" flex={COLS.email} />
            <Th label="Branch" flex={COLS.branch} />
            <Th label="Joined" flex={COLS.joined} />
            <Th label="Actions" flex={COLS.actions} align="right" />
          </View>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
            {filtered.length === 0 ? (
              <View style={styles.empty}>
                <Text style={{ fontSize: 48 }}>🏛️</Text>
                <Text style={styles.emptyTitle}>No shareholders found</Text>
              </View>
            ) : (
              paged.map((item, index) => <Row key={item.id} item={item} index={index} />)
            )}
          </ScrollView>
        </View>
      </ScrollView>

      {/* Combined bottom bar: stats (left) + count & pagination (right) */}
      {filtered.length > 0 && (
        <View style={[styles.bottomBar, isMobile && { flexDirection: 'column', gap: 12 }]}>
          <View style={[styles.statGroup, isMobile && { flexWrap: 'wrap', justifyContent: 'center' }]}>
            {[
              { label: 'Total', value: shareholders.length, color: C.primary },
              { label: 'Active', value: activeCount, color: C.success },
              { label: 'Inactive', value: inactiveCount, color: C.danger },
            ].map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 && <View style={styles.statSep} />}
                <View style={styles.statChip}>
                  <Text style={[styles.statChipNum, { color: s.color }]}>{s.value}</Text>
                  <Text style={styles.statChipLabel}>{s.label}</Text>
                </View>
              </React.Fragment>
            ))}
          </View>
          <View style={styles.pageBtns}>
            <Text style={styles.footerCount}>
              {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
            </Text>
            <TouchableOpacity style={[styles.pageBtn, safePage <= 1 && styles.pageBtnDisabled]} onPress={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}>
              <Text style={[styles.pageBtnTxt, safePage <= 1 && styles.pageBtnTxtDisabled]}>‹ Prev</Text>
            </TouchableOpacity>
            <Text style={styles.pageInfo}>Page {safePage} of {totalPages}</Text>
            <TouchableOpacity style={[styles.pageBtn, safePage >= totalPages && styles.pageBtnDisabled]} onPress={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>
              <Text style={[styles.pageBtnTxt, safePage >= totalPages && styles.pageBtnTxtDisabled]}>Next ›</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* ── Register / Edit right drawer ── */}
      <Modal visible={drawerOpen} animationType="fade" transparent onRequestClose={closeDrawer}>
        <View style={wi.root}>
          <Pressable style={wi.backdrop} onPress={closeDrawer} />
          <Animated.View style={[wi.drawer, { width: drawerW, transform: [{ translateX: slideX }] }]}>
            <View style={wi.header}>
              <TouchableOpacity style={wi.closeBtn} onPress={closeDrawer}><Text style={wi.closeTxt}>✕</Text></TouchableOpacity>
              <Text style={wi.title}>{editingId ? 'Edit Shareholder' : 'Register Shareholder'}</Text>
              <View style={{ width: 36 }} />
            </View>

            <ScrollView style={{ flex: 1 }} contentContainerStyle={wi.scroll} keyboardShouldPersistTaps="handled">
              {/* Profile image */}
              <View style={styles.imageSection}>
                <View style={styles.imagePicker}>
                  {imageUri
                    ? <Image source={{ uri: imageUri }} style={styles.previewImage} />
                    : <View style={styles.imagePlaceholder}><Text style={styles.imagePlaceholderIcon}>📷</Text><Text style={styles.imagePlaceholderText}>Upload Photo</Text></View>}
                  <input
                    ref={fileInputRef as any}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange as any}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                  />
                </View>
                {imageUri && (
                  <TouchableOpacity style={styles.removeImageBtn} onPress={() => { setImageUri(null); setImageFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                    <Text style={styles.removeImageText}>✕ Remove</Text>
                  </TouchableOpacity>
                )}
              </View>

              <Field label="Full Name" field="name" placeholder="Enter full name" value={form.name} error={errors.name} onChange={handleChange} />
              <Field label="Contact Number" field="contact" placeholder="+91 XXXXX XXXXX" value={form.contact} error={errors.contact} onChange={handleChange} keyboardType="phone-pad" />
              <Field label="Email Address" field="email" placeholder="example@email.com" value={form.email} error={errors.email} onChange={handleChange} keyboardType="email-address" readonly={!!editingId} />

              {/* Branch */}
              <View style={styles.fieldWrap}>
                <Text style={styles.label}>Branch *</Text>
                <View style={[styles.dropdownWrapper, branchError ? styles.inputError : null]}>
                  <select
                    value={selectedBranchId}
                    onChange={(e: any) => { setSelectedBranchId(e.target.value); if (e.target.value) setBranchError(''); }}
                    style={dropdownStyle}
                  >
                    <option value="">— Select Branch —</option>
                    {branches.map(b => <option key={b.uid} value={b.branchId}>{b.storename || b.branchId}</option>)}
                  </select>
                </View>
                {branchError ? <Text style={styles.errorText}>{branchError}</Text> : null}
                {selectedBranchId ? <Text style={styles.branchHint}>ID: {selectedBranchId}</Text> : null}
              </View>

              {/* Password fields — create only */}
              {!editingId && (
                <>
                  <View style={styles.fieldWrap}>
                    <Text style={styles.label}>Password</Text>
                    <View style={[styles.inputRow, errors.password ? styles.inputError : null]}>
                      <TextInput
                        style={styles.inputFlex}
                        placeholder="Min. 6 characters"
                        placeholderTextColor="#aaa"
                        value={form.password}
                        onChangeText={(v) => { setForm(f => ({ ...f, password: v })); if (errors.password) setErrors(e => ({ ...e, password: undefined })); }}
                        secureTextEntry={!showPassword}
                      />
                      <TouchableOpacity onPress={() => setShowPassword(s => !s)} style={styles.eyeBtn}>
                        <Text style={styles.eyeText}>{showPassword ? '🙈' : '👁️'}</Text>
                      </TouchableOpacity>
                    </View>
                    {errors.password ? <Text style={styles.errorText}>{errors.password}</Text> : null}
                  </View>

                  <View style={styles.fieldWrap}>
                    <Text style={styles.label}>Confirm Password</Text>
                    <View style={[styles.inputRow, errors.confirmPassword ? styles.inputError : null]}>
                      <TextInput
                        style={styles.inputFlex}
                        placeholder="Re-enter password"
                        placeholderTextColor="#aaa"
                        value={form.confirmPassword}
                        onChangeText={(v) => { setForm(f => ({ ...f, confirmPassword: v })); if (errors.confirmPassword) setErrors(e => ({ ...e, confirmPassword: undefined })); }}
                        secureTextEntry={!showPassword}
                      />
                    </View>
                    {errors.confirmPassword ? <Text style={styles.errorText}>{errors.confirmPassword}</Text> : null}
                  </View>
                </>
              )}

              <TouchableOpacity style={[styles.submitBtn, loading && styles.submitBtnDisabled]} onPress={handleSubmit} disabled={loading}>
                {loading ? <ActivityIndicator color="#fff" /> : <Text style={styles.submitBtnText}>{editingId ? 'Update Shareholder' : 'Register Shareholder'}</Text>}
              </TouchableOpacity>
              <View style={{ height: 20 }} />
            </ScrollView>
          </Animated.View>
        </View>
      </Modal>

      {/* ── Delete confirm modal ── */}
      <Modal visible={!!deleteTarget} animationType="fade" transparent>
        <Pressable style={cf.overlay} onPress={() => !deleting && setDeleteTarget(null)}>
          <Pressable style={cf.card} onPress={() => {}}>
            <View style={cf.iconWrap}><Text style={{ fontSize: 26 }}>🗑️</Text></View>
            <Text style={cf.title}>Delete shareholder?</Text>
            <Text style={cf.body}>
              {deleteTarget ? `This removes "${deleteTarget.name || 'this shareholder'}" from the list. The Auth account is not deleted. This cannot be undone.` : ''}
            </Text>
            <View style={cf.actions}>
              <TouchableOpacity style={cf.cancelBtn} onPress={() => setDeleteTarget(null)} disabled={deleting}><Text style={cf.cancelTxt}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={[cf.confirmBtn, deleting && { opacity: 0.6 }]} onPress={confirmDelete} disabled={deleting}>
                {deleting ? <ActivityIndicator color="#fff" size="small" /> : <Text style={cf.confirmTxt}>Delete</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* ── Toast ── */}
      {toast && (
        <View style={[styles.toast, { backgroundColor: toast.type === 'error' ? C.danger : C.success }]}>
          <Text style={styles.toastTxt}>{toast.type === 'error' ? '✕ ' : '✓ '}{toast.msg}</Text>
        </View>
      )}
    </View>
  );
};

export default ShareholderScreen;

// Native CSS style for the HTML <select> element
const dropdownStyle: React.CSSProperties = {
  width: '100%', height: 48, backgroundColor: '#141E18', borderRadius: 10,
  border: '1.5px solid #e2e8f0', paddingLeft: 14, paddingRight: 14, fontSize: 15,
  color: '#E2EDE6', cursor: 'pointer', outline: 'none', appearance: 'auto',
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.bg, padding: 16 },

  controls: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: '#0A1712', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)', flexWrap: 'wrap' },
  tbTitle: { flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 'auto' as any },
  tbTitleText: { fontSize: 15, fontWeight: '800', color: '#fff' },
  tbTitleSub: { fontSize: 11, color: '#8BAAB8', marginTop: 1 },
  searchBox: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 8, borderWidth: 1, borderColor: 'rgba(33,128,141,0.25)', paddingHorizontal: 10, height: 36, minWidth: 160 },
  searchInput: { flex: 1, fontSize: 13, color: '#e2f0f2', outlineStyle: 'none' as any },
  filterRow: { flexDirection: 'row', gap: 6 },
  filterChip: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 20, backgroundColor: 'transparent', borderWidth: 1, borderColor: 'rgba(33,128,141,0.3)' },
  filterChipActive: { backgroundColor: 'rgba(78,205,196,0.15)', borderColor: '#4ECDC4' },
  filterChipTxt: { fontSize: 12, fontWeight: '600', color: '#8BAAB8' },
  filterChipTxtActive: { color: '#4ECDC4' },
  addBtn: { backgroundColor: '#4ECDC4', paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8 },
  addBtnTxt: { color: '#0A1712', fontWeight: '700', fontSize: 12 },

  empty: { alignItems: 'center', paddingTop: 60 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: C.text, marginTop: 12 },

  bottomBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#17201B', borderRadius: 12, borderWidth: 1, borderColor: C.border, paddingVertical: 9, paddingHorizontal: 14, marginTop: 8, flexWrap: 'wrap', gap: 10 },
  statGroup: { flexDirection: 'row', alignItems: 'center' },
  statChip: { flexDirection: 'row', alignItems: 'baseline', gap: 5, paddingHorizontal: 12 },
  statChipNum: { fontSize: 16, fontWeight: '800' },
  statChipLabel: { fontSize: 11, color: C.muted, fontWeight: '600' },
  statSep: { width: 1, height: 16, backgroundColor: C.border },
  pageBtns: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  footerCount: { fontSize: 12, color: C.muted, fontWeight: '600', marginRight: 4 },
  pageBtn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 9, borderWidth: 1, borderColor: C.border, backgroundColor: '#17201B' },
  pageBtnDisabled: { backgroundColor: '#141E18', borderColor: 'rgba(255,255,255,0.06)' },
  pageBtnTxt: { fontSize: 13, fontWeight: '700', color: C.primary },
  pageBtnTxtDisabled: { color: '#cbd5e1' },
  pageInfo: { fontSize: 13, fontWeight: '600', color: C.text, minWidth: 96, textAlign: 'center' },

  toast: { position: 'absolute', bottom: 24, alignSelf: 'center', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.2, shadowRadius: 8, elevation: 6 },
  toastTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },

  // form (used inside drawer)
  imageSection: { alignItems: 'center', marginBottom: 20 },
  imagePicker: { width: 110, height: 110, borderRadius: 55, overflow: 'hidden', borderWidth: 2, borderColor: '#e0e0e0', borderStyle: 'dashed', position: 'relative' },
  previewImage: { width: '100%', height: '100%' },
  imagePlaceholder: { flex: 1, backgroundColor: '#f4f4f4', alignItems: 'center', justifyContent: 'center', gap: 4 },
  imagePlaceholderIcon: { fontSize: 28 },
  imagePlaceholderText: { fontSize: 11, color: '#aaa', fontWeight: '600' },
  removeImageBtn: { marginTop: 8 },
  removeImageText: { fontSize: 13, color: '#e53935', fontWeight: '600' },
  fieldWrap: { marginBottom: 16 },
  label: { fontSize: 13, fontWeight: '700', color: '#444', marginBottom: 6 },
  input: { height: 48, backgroundColor: '#141E18', borderRadius: 10, paddingHorizontal: 14, fontSize: 15, color: '#E2EDE6', borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', outlineStyle: 'none' as any },
  inputRow: { flexDirection: 'row', alignItems: 'center', height: 48, backgroundColor: '#141E18', borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', paddingHorizontal: 14 },
  inputFlex: { flex: 1, fontSize: 15, color: '#E2EDE6', outlineStyle: 'none' as any },
  eyeBtn: { padding: 4 },
  eyeText: { fontSize: 18 },
  inputError: { borderColor: '#e53935', backgroundColor: '#fff5f5' },
  errorText: { fontSize: 12, color: '#e53935', marginTop: 4, marginLeft: 2 },
  submitBtn: { height: 52, backgroundColor: C.primary, borderRadius: 12, alignItems: 'center', justifyContent: 'center', marginTop: 8 },
  submitBtnDisabled: { opacity: 0.6 },
  submitBtnText: { color: '#fff', fontSize: 16, fontWeight: '700' },
  dropdownWrapper: { borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)' },
  branchHint: { fontSize: 11, color: '#7A9A80', marginTop: 4, marginLeft: 2 },
});

// ── Table styles ──
const tb = StyleSheet.create({
  table: { flex: 1, backgroundColor: '#17201B', borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: C.border },
  headerRow: { flexDirection: 'row', backgroundColor: '#0A1712', borderBottomWidth: 1, borderBottomColor: C.border, paddingHorizontal: 14, paddingVertical: 12 },
  th: { justifyContent: 'center' },
  thTxt: { fontSize: 11, fontWeight: '800', color: C.muted, letterSpacing: 0.6, textTransform: 'uppercase' as const },
  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#141E18' },
  td: { justifyContent: 'center', paddingRight: 8 },
  snTxt: { fontSize: 12, fontWeight: '700', color: C.muted },
  cellTxt: { fontSize: 13, color: C.text },
  avatar: { width: 38, height: 38, borderRadius: 19, backgroundColor: '#e2e8f0' },
  avatarTxt: { color: '#fff', fontSize: 15, fontWeight: '700' },
  name: { fontSize: 14, fontWeight: '600', color: C.text },
  idTxt: { fontSize: 10, color: C.muted, marginTop: 1 },
  tgl: { borderRadius: 14, borderWidth: 1, paddingHorizontal: 10, paddingVertical: 5 },
  tglTxt: { fontSize: 11, fontWeight: '700' },
  editBtn: { backgroundColor: C.primaryLight, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14 },
  editTxt: { color: C.primary, fontWeight: '700', fontSize: 12 },
  delBtn: { backgroundColor: C.dangerLight, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14 },
  delTxt: { color: C.danger, fontWeight: '700', fontSize: 12 },
});

// ── Right-drawer styles ──
const wi = StyleSheet.create({
  root: { flex: 1, flexDirection: 'row', justifyContent: 'flex-end' },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(15,23,42,0.45)' },
  drawer: { height: '100%', backgroundColor: '#17201B', shadowColor: '#000', shadowOffset: { width: -4, height: 0 }, shadowOpacity: 0.18, shadowRadius: 20, elevation: 16 },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#17201B', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 14, borderBottomWidth: 1, borderBottomColor: C.border, gap: 12 },
  closeBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#141E18', justifyContent: 'center', alignItems: 'center' },
  closeTxt: { fontSize: 14, color: C.muted, fontWeight: '600' },
  title: { flex: 1, fontSize: 18, fontWeight: '700', color: C.text, textAlign: 'center' },
  scroll: { padding: 20, paddingBottom: 40 },
});

// ── Delete confirm modal styles ──
const cf = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: { backgroundColor: '#17201B', borderRadius: 16, padding: 24, width: '100%', maxWidth: 380, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.2, shadowRadius: 24, elevation: 10 },
  iconWrap: { width: 56, height: 56, borderRadius: 28, backgroundColor: C.dangerLight, justifyContent: 'center', alignItems: 'center', marginBottom: 14 },
  title: { fontSize: 18, fontWeight: '700', color: C.text, marginBottom: 6 },
  body: { fontSize: 13, color: C.muted, textAlign: 'center', lineHeight: 19, marginBottom: 20 },
  actions: { flexDirection: 'row', gap: 10, width: '100%' },
  cancelBtn: { flex: 1, paddingVertical: 11, borderRadius: 10, borderWidth: 1, borderColor: C.border, alignItems: 'center' },
  cancelTxt: { color: C.text, fontWeight: '600', fontSize: 14 },
  confirmBtn: { flex: 1, paddingVertical: 11, borderRadius: 10, backgroundColor: C.danger, alignItems: 'center' },
  confirmTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },
});
